/**
 * edj-zoom-magnifier.js
 * Effet loupe au hover desktop sur l'image principale produit.
 * Item 9 (VAGUE 3) — VAGUE 5 Fix 3 — EDJ Shopify theme
 *
 * Comportement :
 *   - Desktop only (pointer:fine et hover:hover)
 *   - Au mouseenter sur la zone main : crée un overlay rond avec
 *     background-image haute-res et background-size augmenté
 *   - Au mousemove : suit le curseur, met à jour background-position
 *   - Au mouseleave : masque l'overlay (mais le garde dans le DOM)
 *
 * Mobile : ne fait rien (le tap ouvre toujours le zoom-dialog full-screen
 * via la galerie, comportement existant).
 *
 * Cible : <img class="edj-product-gallery__image"> dans une slide active.
 * Désactivé si <html data-zoom="false"> ou si la slide active est une vidéo.
 *
 * VAGUE 5 Fix 3 — corrections par rapport à VAGUE 3 :
 *   - Bind sur le conteneur __main (et non sur chaque <img>) : un seul
 *     handler stable, plus robuste au switch de slide variant.
 *   - Recherche dynamique de la slide active à chaque mouseenter / mousemove
 *     (au lieu de capturer img/parent en closure).
 *   - Lens persistante dans le DOM (display: none au lieu de remove) :
 *     évite tout problème de timing setTimeout / re-create.
 *   - Skip explicite des slides vidéo (pas de loupe sur lecteur vidéo).
 *   - Logging debug si window.EDJ_DEBUG === true.
 */
(function () {
  'use strict';

  var DEBUG = !!window.EDJ_DEBUG;
  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[edj-magnifier]'].concat([].slice.call(arguments))); } catch (_) {}
  }

  // Désactivé sur tactile / petits écrans (le dialog mobile prend le relais)
  var SUPPORTS_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
  if (!SUPPORTS_HOVER.matches) {
    log('hover/pointer non supporté → magnifier inactif');
    return;
  }

  var ZOOM = 2;        // facteur de zoom 2× (équivalent background-size 200%)
  var LENS_SIZE = 200; // px

  function createLens() {
    var l = document.createElement('div');
    l.className = 'edj-zoom-lens';
    l.setAttribute('aria-hidden', 'true');
    l.style.cssText = [
      'position:absolute',
      'pointer-events:none',
      'border-radius:50%',
      'border:2px solid rgba(255,255,255,.85)',
      'box-shadow:0 4px 18px rgba(0,0,0,.25)',
      'background-repeat:no-repeat',
      'opacity:0',
      'transition:opacity .12s ease',
      'z-index:10',
      'left:0',
      'top:0',
      'width:' + LENS_SIZE + 'px',
      'height:' + LENS_SIZE + 'px',
      'display:none'
    ].join(';');
    return l;
  }

  function getActiveImage(main) {
    var slide = main.querySelector('[data-edj-slide].is-active');
    if (!slide) return null;
    if (slide.classList.contains('edj-product-gallery__slide--video')) return null;
    // VAGUE 6 : le filtre Liquid image_tag n'applique pas toujours la classe
    // demandée → on cible n'importe quel <img> direct dans la slide active.
    // Fallback ordonné : img avec la classe attendue, puis n'importe quel img.
    return slide.querySelector('img.edj-product-gallery__image') || slide.querySelector('img');
  }

  function getHighResUrl(img) {
    return img.currentSrc || img.src || '';
  }

  function bindMain(main) {
    if (!main || main.dataset.edjMagnifierBound === '1') return;
    main.dataset.edjMagnifierBound = '1';

    // S'assure que main est en position relative (déjà le cas dans le CSS)
    var cs = getComputedStyle(main);
    if (cs.position === 'static') main.style.position = 'relative';

    // Lens unique persistante
    var lens = createLens();
    main.appendChild(lens);
    log('bind main', main, 'lens injectée');

    function show() {
      var img = getActiveImage(main);
      if (!img) {
        log('mouseenter mais aucune image active (slide vidéo ?)');
        return;
      }
      var url = getHighResUrl(img);
      if (!url) return;
      lens.style.backgroundImage = 'url("' + url.replace(/"/g, '\\"') + '")';
      var w = img.clientWidth * ZOOM;
      var h = img.clientHeight * ZOOM;
      lens.style.backgroundSize = w + 'px ' + h + 'px';
      lens.style.display = 'block';
      // requestAnimationFrame pour laisser le browser appliquer display avant opacity
      requestAnimationFrame(function () { lens.style.opacity = '1'; });
      log('show lens', { url: url, w: w, h: h });
    }

    function move(e) {
      var img = getActiveImage(main);
      if (!img) return;
      var rect = img.getBoundingClientRect();
      var mainRect = main.getBoundingClientRect();
      var x = e.clientX - rect.left;
      var y = e.clientY - rect.top;
      // Si en dehors de l'image (peut arriver près des bords du __main si l'image
      // ne le remplit pas exactement), on cache la lens
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
        lens.style.opacity = '0';
        return;
      }
      lens.style.opacity = '1';
      var lensLeft = (rect.left - mainRect.left) + x - LENS_SIZE / 2;
      var lensTop = (rect.top - mainRect.top) + y - LENS_SIZE / 2;
      lens.style.left = lensLeft + 'px';
      lens.style.top = lensTop + 'px';
      var bgX = -((x * ZOOM) - LENS_SIZE / 2);
      var bgY = -((y * ZOOM) - LENS_SIZE / 2);
      lens.style.backgroundPosition = bgX + 'px ' + bgY + 'px';
    }

    function hide() {
      lens.style.opacity = '0';
      // On garde lens dans le DOM (display: block stays) — la prochaine fois,
      // on n'aura pas à recréer et la transition d'opacity sera fluide.
      // Pour économiser le repaint quand pas en cours d'usage, on revient à display:none
      // après la transition.
      setTimeout(function () {
        if (parseFloat(lens.style.opacity) === 0) lens.style.display = 'none';
      }, 200);
    }

    main.addEventListener('mouseenter', show);
    main.addEventListener('mousemove', move);
    main.addEventListener('mouseleave', hide);
  }

  function init() {
    var galleries = document.querySelectorAll('edj-product-gallery, .edj-product-gallery');
    log('init', galleries.length, 'gallery(s) trouvée(s)');
    galleries.forEach(function (gallery) {
      if (gallery.dataset.zoom === 'false') {
        log('zoom désactivé sur', gallery);
        return;
      }
      var main = gallery.querySelector('.edj-product-gallery__main');
      if (!main) {
        log('aucun .edj-product-gallery__main dans', gallery);
        return;
      }
      bindMain(main);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
